import React from 'react';
import { Link } from 'react-router-dom';
import { UsageStatus } from '../../../services/mcpUsageService';

interface TokenLimitBannerProps {
  usageStatus: UsageStatus;
}

/** Format a unix timestamp (seconds) as e.g. "January 1, 2026". */
const formatResetDate = (timestamp: number): string =>
  new Date(timestamp * 1000).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

const TokenLimitBanner: React.FC<TokenLimitBannerProps> = ({ usageStatus }) => {
  const percentUsed = Math.round(
    (usageStatus.tokensUsed / usageStatus.tokenLimit) * 100
  );

  // Both free (calendar month) and paid (billing cycle) users have a date when their limit resets; periodEnd carries it for either tier.
  const isPaid = usageStatus.tier !== 'free';
  const resetDate = usageStatus.periodEnd
    ? formatResetDate(usageStatus.periodEnd)
    : null;

  // Limit exceeded - show blocking banner
  if (usageStatus.isLimitExceeded) {
    return (
      <div className="mx-4 mb-4 rounded-eai-card border border-eai-danger-border bg-eai-danger-surface p-4">
        <div className="flex items-start gap-3">
          <svg
            className="h-6 w-6 text-eai-danger-text flex-shrink-0 mt-0.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <div className="flex-1">
            <h3 className="eai-card-title text-eai-danger-text">
              Monthly Token Limit Reached
            </h3>
            <p className="mt-1 font-inter text-eai-body-sm text-eai-danger-text">
              You&apos;ve used {usageStatus.tokensUsed.toLocaleString()} of your{' '}
              {usageStatus.tokenLimit.toLocaleString()} monthly tokens.
              {resetDate && <> Your limit will reset on {resetDate}.</>}
              {!isPaid ? (
                <> Upgrade to a paid plan for higher limits.</>
              ) : !resetDate ? (
                <> Your limit resets at the start of your next billing period.</>
              ) : null}
            </p>
            <Link
              to="/home/subscriptions"
              className="eai-btn-danger mt-3"
            >
              Upgrade Plan
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Warning banner when approaching limit (>80%)
  if (percentUsed >= 80) {
    return (
      <div className="mx-4 mb-4 rounded-eai-card border border-eai-warning-text/25 bg-eai-brand-peach p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <svg
              className="h-5 w-5 text-eai-warning-text"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <span className="text-eai-body-sm text-eai-ink">
              {percentUsed}% of your monthly token limit used (
              {usageStatus.remainingTokens.toLocaleString()} remaining)
              {resetDate && <> · Your limit will reset on {resetDate}.</>}
            </span>
          </div>
          <Link
            to="/home/subscriptions"
            className="text-eai-body-sm font-medium text-eai-warning-text underline transition-colors hover:text-eai-ink"
          >
            Upgrade
          </Link>
        </div>
      </div>
    );
  }

  // No banner needed when under 80%
  return null;
};

export default TokenLimitBanner;
